import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { setSelectedFilesContentExpanded } from '@/chat-common/store/chat-ux.slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { isTerminalProcessingStatus } from '@/user_files/utils/processing-status'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import {
  selectAgentConversationFormAttachedPendingUploads,
  selectAgentConversationFormUserFileIds,
  selectAgentConversationFormWritingSampleFileIds,
} from '@/agent/chat_form/store/selectors'
import { kUppyUploadAllowedFileTypes } from '@/uppy-file-upload/constants'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import type { FileSelectionTarget } from '@/agent/chat_form/store/schemas'

const ASSISTANT_UPLOADS_FOLDER_NAME = 'Assistant Uploads'

type ChatWindowUserFilesUploadProps = {
  conversationId: string
  matterId: string | null
  /** What an uploaded file attaches to. Defaults to the multi-file user_files (RAG) behavior. */
  uploadTarget?: FileSelectionTarget
}

export interface ChatWindowUserFilesUploadRef {
  openSystemFileSelector: () => void
  addFiles: (files: File[]) => void
  cancelUpload: (clientTmpId: string) => void
}

const ChatWindowUserFilesUpload = forwardRef<ChatWindowUserFilesUploadRef, ChatWindowUserFilesUploadProps>(function ChatWindowUserFilesUpload(
  { conversationId, matterId, uploadTarget = 'user-files' },
  ref
) {
  const isWritingSample = uploadTarget === 'writing-sample'
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const attachedFileIds = useAppSelector((state) =>
    isWritingSample
      ? selectAgentConversationFormWritingSampleFileIds(state, { conversationId })
      : selectAgentConversationFormUserFileIds(state, { conversationId })
  )
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null)
  const uploadFolderIdRef = useRef<string | null>(null)
  const currentMatterIdRef = useRef<string | null>(matterId)
  const filesWaitingForUploadFolderRef = useRef<File[] | null>(null)
  const openPickerAfterFolderReadyRef = useRef(false)

  const fileIdsNeedingPolling = useMemo(
    () =>
      attachedFileIds.filter((fileId) => {
        const uploadFile = uploadFilesByServerFileId[fileId]
        if (!uploadFile) return false
        if (uploadFile.client.uploadStatus !== 'uploaded') return false
        return !isTerminalProcessingStatus(uploadFile.backend?.record.processing_status)
      }),
    [uploadFilesByServerFileId, attachedFileIds]
  )

  useUserFilesPollingProcessor({
    sourceId: `assistant-${uploadTarget}-upload-${conversationId}`,
    fileIds: fileIdsNeedingPolling,
    enabled: fileIdsNeedingPolling.length > 0,
  })

  const {
    openUploadPicker,
    disabled: driveUploadDisabled,
    fileInputRef,
    addFiles,
    onFilesPicked,
    cancelUpload,
    conflictDialog,
  } = useDriveUpload({
    folderId: uploadFolderId,
    onFileRegistered: ({ clientTmpId }) => {
      dispatch(AgentConversationFormActions.addPendingUserFileUpload({ conversationId, clientTmpId, target: uploadTarget }))
      dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
    },
    onFileInitialized: ({ clientTmpId, serverFileId }) => {
      const attachedPendingUploads = selectAgentConversationFormAttachedPendingUploads(store.getState(), { conversationId })
      if (!attachedPendingUploads.some((upload) => upload.clientTmpId === clientTmpId)) return

      dispatch(
        isWritingSample
          ? AgentConversationFormActions.addWritingSampleFile({ conversationId, userFileId: serverFileId })
          : AgentConversationFormActions.addUserFile({ conversationId, userFileId: serverFileId })
      )
      dispatch(AgentConversationFormActions.removePendingUserFileUpload({ conversationId, clientTmpId }))
      dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
    },
    onUploadCompleted: ({ folderId: completedFolderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: completedFolderId }))
    },
    onUploadFailed: ({ folderId: failedFolderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: failedFolderId }))
    },
  })

  const disabled = !matterId || (Boolean(uploadFolderId) && driveUploadDisabled)

  const prepareUploadFolder = useCallback(async (): Promise<string | null> => {
    if (!matterId) return null
    if (uploadFolderIdRef.current) return uploadFolderIdRef.current

    const requestedMatterId = matterId
    try {
      const folderId = await getOrCreateUserFilesUploadFolderId({
        dispatch,
        folderName: ASSISTANT_UPLOADS_FOLDER_NAME,
        getState: store.getState,
        matterId: requestedMatterId,
      })
      if (currentMatterIdRef.current !== requestedMatterId) return null

      uploadFolderIdRef.current = folderId
      setUploadFolderId(folderId)
      return folderId
    } catch (error) {
      console.error('Failed to prepare Matter Files upload folder:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'File could not be uploaded.',
          durationMs: kToastDurationMediumMs,
        })
      )
      return null
    }
  }, [dispatch, matterId, store])

  useEffect(() => {
    currentMatterIdRef.current = matterId
    uploadFolderIdRef.current = null
    filesWaitingForUploadFolderRef.current = null
    openPickerAfterFolderReadyRef.current = false
    setUploadFolderId(null)
  }, [matterId])

  // Continue the original upload intent after the folder id has reached useDriveUpload.
  useEffect(() => {
    if (!uploadFolderId) return

    if (openPickerAfterFolderReadyRef.current) {
      openPickerAfterFolderReadyRef.current = false
      openUploadPicker()
    }

    const files = filesWaitingForUploadFolderRef.current
    if (!files) return
    filesWaitingForUploadFolderRef.current = null
    addFiles(files)
  }, [addFiles, openUploadPicker, uploadFolderId])

  const addFilesAfterUploadFolderReady = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || disabled) return

      dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
      const folderId = await prepareUploadFolder()
      if (!folderId) return

      if (uploadFolderId === folderId) {
        addFiles(files)
        return
      }

      filesWaitingForUploadFolderRef.current = [...(filesWaitingForUploadFolderRef.current ?? []), ...files]
    },
    [addFiles, conversationId, disabled, dispatch, prepareUploadFolder, uploadFolderId]
  )

  const openSystemFileSelectorAfterUploadFolderReady = useCallback(async () => {
    if (disabled) return

    const folderId = await prepareUploadFolder()
    if (!folderId) return

    if (uploadFolderId === folderId) {
      openUploadPicker()
      return
    }

    openPickerAfterFolderReadyRef.current = true
  }, [disabled, openUploadPicker, prepareUploadFolder, uploadFolderId])

  useImperativeHandle(ref, () => ({
    openSystemFileSelector: () => {
      void openSystemFileSelectorAfterUploadFolderReady()
    },
    addFiles: (files) => {
      void addFilesAfterUploadFolderReady(files)
    },
    cancelUpload: (clientTmpId) => {
      cancelUpload(clientTmpId)
    },
  }))

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
    }
    onFilesPicked(event)
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple={!isWritingSample}
        accept={kUppyUploadAllowedFileTypes.join(',')}
        disabled={disabled}
        className="hidden"
        onChange={handleFilesPicked}
      />
      <ConflictModal {...conflictDialog} operation="upload" />
    </>
  )
})

export default ChatWindowUserFilesUpload
